import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, CircleDot, Clock, Layers } from "lucide-react";
import { useSelector } from "react-redux";
import { selectAppUser } from "../../../redux/appAuthSlice";
import BoardOfferRow from "./BoardOfferRow";
import FavoriteTeamStar from "../FavoriteTeamStar";
import {
  buildMarketOutcomeBook,
  calculateTimeRemaining,
  fixtureHasFavoriteTeam,
  getOfferMatchedCash,
  groupOffersByMarket,
  teamMatchesFavorite,
} from "../../../utils/p2pHelpers";
import { labelForP2PField } from "../../../data/p2pFieldLabels";
import { formatAppTime, formatAppDate } from "../../../utils/appTimeFormat";

const Crest = ({ team, align, favoriteTeam }) => {
  const [failed, setFailed] = useState(false);
  const isFavorite = teamMatchesFavorite(team, favoriteTeam);
  return (
    <span className={`pmb-fix-team pmb-fix-team-${align}${isFavorite ? " pmb-fix-team-fav" : ""}`}>
      <span className="pmb-fix-crest" aria-hidden>
        {team?.logo && !failed ? (
          <img src={team.logo} alt="" onError={() => setFailed(true)} />
        ) : (
          <span>{(team?.name || "?").slice(0, 1).toUpperCase()}</span>
        )}
        {isFavorite ? (
          <span className="pmb-fix-team-star">
            <FavoriteTeamStar size={8} />
          </span>
        ) : null}
      </span>
      <span className="pmb-fix-team-name" title={team?.name}>
        {team?.name || (align === "home" ? "Home" : "Away")}
      </span>
    </span>
  );
};

const fmtCash = (value) => {
  const v = Number(value) || 0;
  if (v >= 100000) return `${Math.round(v / 1000)}k`;
  if (v >= 10000) return `${(v / 1000).toFixed(1)}k`;
  return v.toLocaleString();
};

/**
 * Exchange-style outcome book for one market on a fixture: how the backing
 * cash is distributed across picked outcomes (imbalance bar + cells).
 */
const MarketOutcomeBook = ({ offers, fixture }) => {
  const book = buildMarketOutcomeBook(offers, fixture);
  if (book.length < 2) return null;
  const totalOpen = book.reduce((s, r) => s + r.openCash, 0);
  if (totalOpen <= 0) return null;

  return (
    <div className="pmb-book" aria-label="Outcome book">
      <span className="pmb-book-bar" aria-hidden>
        {book.map((row) => (
          <i
            key={row.label}
            style={{ width: `${(row.openCash / totalOpen) * 100}%` }}
            title={`${row.label} · ${fmtCash(row.openCash)} open`}
          />
        ))}
      </span>
      <span className="pmb-book-cells">
        {book.map((row) => (
          <span key={row.label} className="pmb-book-cell">
            <strong title={row.label}>{row.label}</strong>
            <em>{fmtCash(row.openCash)}</em>
            <b>
              {row.offerCount} offer{row.offerCount === 1 ? "" : "s"}
            </b>
          </span>
        ))}
      </span>
    </div>
  );
};

const fixtureScore = (fixture, isLive, isFinished) => {
  const goals = fixture?.goals || fixture?.score?.fulltime || {};
  const h = goals.home ?? goals.homeTeam;
  const a = goals.away ?? goals.awayTeam;
  if ((isLive || isFinished) && h != null && a != null) {
    return `${h} – ${a}`;
  }
  return null;
};

/**
 * One match line on the exchange board. Collapsed: teams + score/status +
 * liquidity summary. Expanded: offers grouped by market (selectable rows).
 */
const BoardFixtureRow = ({
  entry,
  expanded,
  onToggle,
  selectedId,
  onSelectOffer,
}) => {
  const user = useSelector(selectAppUser);
  const favoriteTeam = user?.favoriteTeam;
  const { fixture, offers, stats } = entry;
  const home = fixture?.teams?.home || {};
  const away = fixture?.teams?.away || {};
  const showsFavoriteStar = fixtureHasFavoriteTeam(fixture, favoriteTeam);

  const isLive = stats.status === "Live";
  const isFinished = stats.status === "Finished";
  const shortStatus = (fixture?.status?.short || "NS").toUpperCase();
  const score = fixtureScore(fixture, isLive, isFinished);

  const [countdown, setCountdown] = useState("");
  useEffect(() => {
    if (isLive || isFinished) {
      setCountdown("");
      return undefined;
    }
    const tick = () => setCountdown(calculateTimeRemaining(fixture?.date));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [fixture?.date, isLive, isFinished]);

  const marketGroups = useMemo(() => groupOffersByMarket(offers), [offers]);

  const matchedCash = useMemo(
    () => offers.reduce((sum, o) => sum + getOfferMatchedCash(o), 0),
    [offers]
  );
  const depthTotal = matchedCash + (stats.openCash || 0);

  const summary = [
    `${stats.offerCount} offer${stats.offerCount === 1 ? "" : "s"}`,
    `${stats.marketCount} market${stats.marketCount === 1 ? "" : "s"}`,
  ];
  if (stats.openCash > 0) {
    summary.push(`${fmtCash(stats.openCash)} open`);
  }
  if (matchedCash > 0) {
    summary.push(`${fmtCash(matchedCash)} matched`);
  }

  const timeMeta = isLive
    ? "In play"
    : isFinished
    ? "Full time"
    : countdown
    ? `Closes in ${countdown}`
    : `${formatAppDate(fixture?.date)} · ${formatAppTime(fixture?.date)}`;

  return (
    <div
      className={`pmb-fix${expanded ? " pmb-fix-expanded" : ""}${
        isLive ? " pmb-fix-live" : ""
      }`}
    >
      <button
        type="button"
        className="pmb-fix-head"
        onClick={() => onToggle?.(entry.fixtureId)}
        aria-expanded={expanded}
      >
        <span className="pmb-fix-accent" aria-hidden />

        <span className="pmb-fix-main">
          <span className="pmb-fix-meta">
            <span className="pmb-fix-meta-left">
              <span className={`pmb-fix-status pmb-fix-status-${stats.status.toLowerCase()}`}>
                {isLive && <CircleDot size={8} aria-hidden />}
                {stats.status}
              </span>
              {showsFavoriteStar ? (
                <FavoriteTeamStar size={10} className="pmb-fix-fav-star" />
              ) : null}
            </span>
            <span className="pmb-fix-time">
              {!isLive && !isFinished && countdown ? <Clock size={9} aria-hidden /> : null}
              {timeMeta}
            </span>
          </span>

          <span className="pmb-fix-match">
            <Crest team={home} align="home" favoriteTeam={favoriteTeam} />
            <span className={`pmb-fix-score${isLive ? " pmb-fix-score-live" : ""}`}>
              {score ? (
                <span className="pmb-fix-score-num">{score}</span>
              ) : (
                <>
                  <span className="pmb-fix-score-status">{shortStatus}</span>
                  <span className="pmb-fix-score-kick">{formatAppTime(fixture?.date)}</span>
                </>
              )}
            </span>
            <Crest team={away} align="away" favoriteTeam={favoriteTeam} />
          </span>

          <span className="pmb-fix-foot">
            <span className="pmb-fix-summary">{summary.join("  ·  ")}</span>
            <span className="pmb-fix-chev" aria-hidden>
              <ChevronDown size={15} />
            </span>
          </span>

          {depthTotal > 0 ? (
            <span
              className="pmb-fix-depth"
              aria-hidden
              title={`${fmtCash(matchedCash)} matched · ${fmtCash(stats.openCash)} open`}
            >
              <i
                className="pmb-fix-depth-matched"
                style={{ width: `${(matchedCash / depthTotal) * 100}%` }}
              />
              <i
                className="pmb-fix-depth-open"
                style={{ width: `${((stats.openCash || 0) / depthTotal) * 100}%` }}
              />
            </span>
          ) : null}
        </span>
      </button>

      {expanded && (
        <div className="pmb-markets">
          {marketGroups.map((group) => (
            <div key={group.section} className="pmb-market">
              <div className="pmb-market-head">
                <Layers size={11} aria-hidden />
                <span className="pmb-market-name">
                  {labelForP2PField(group.section) || group.section}
                </span>
                <span className="pmb-market-count">{group.offers.length}</span>
                {group.openCash > 0 ? (
                  <span className="pmb-market-open">
                    {fmtCash(group.openCash)} open
                  </span>
                ) : null}
              </div>
              <MarketOutcomeBook offers={group.offers} fixture={fixture} />
              <div className="pmb-market-body">
                {group.offers.map((offer) => (
                  <BoardOfferRow
                    key={offer._id}
                    bet={offer}
                    selected={offer._id === selectedId}
                    onSelect={onSelectOffer}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(BoardFixtureRow);
